import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BasicInputWithExtras } from '../../../../components/common/inputs/BasicInputWithExtras';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import { sellTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { sellTractorSurveySteps } from '../../../../constants/survey/sellTractorSurvey';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { InputValue } from '../../../../interfaces/Element.interfaces';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { PriceHelperText } from '../../components/PriceHelperText';
import { SellTractorSurveyPageWrapper } from '../SellTractorSurveyPageWrapper';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { formatPrice } from '../../../../utils/formatter/formatPrice.util';
import { Button } from '../../../../components/common/buttons/Button';
import { useInfiniteOpenMarketProducts } from '../../../../hooks/api/product/queries/useInfiniteOpenMarketProducts';
import { ProductStatusConstant } from '../../../../constants/product/product.constant';
import { ampli } from '../../../../ampli';

export const SellTractorSurveyHopeSellingPricePage = () => {
  const navigate = useNavigate();

  const [hopeSellingPrice, setHopeSellingPrice] = useState<number>();
  const [isShowErrorModal, setIsShowErrorModal] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;
  const userSellTractorSurveySelector = useAppSelector((state) => state.userSellTractorSurvey);

  const isRegistration = !userSellTractorSurveySelector.sellTractorIdForEdit;
  const currentStep = isRegistration ? sellTractorSurveySteps.STEP2 : sellTractorSurveySteps.STEP7;
  const steps = Object.keys(sellTractorSurveySteps);
  const registrationSteps = steps.slice(0, 3);

  const { brand, model, manufacturedYear, operationTime, sellTractorMachineInfo, machineType } =
    userSellTractorSurveySelector;

  const { data: openMarketProducts } = useInfiniteOpenMarketProducts({
    searchParam: {
      status: `${ProductStatusConstant.ONSALE},${ProductStatusConstant.SOLDOUT}`,
      count: 100,
      machineType,
      machineId: sellTractorMachineInfo?.id as string,
    },
    options: {
      enabled: !!sellTractorMachineInfo?.id,
    },
  });

  useEffect(() => {
    const { hopeSellingPrice } = userSellTractorSurveySelector;

    if (hopeSellingPrice) {
      setHopeSellingPrice(hopeSellingPrice);
    }
  }, [userSellTractorSurveySelector.hopeSellingPrice]);

  return (
    <>
      <SellTractorSurveyPageWrapper
        headerTitle={'판매 가격을 입력해 주세요'}
        canConfirm
        confirmHandler={() => {
          if (!hopeSellingPrice) {
            setIsShowErrorModal(true);
            return;
          }
          dispatch(userSellTractorSurveyActions.changeHopeSellingPrice(hopeSellingPrice));
          navigate(sellTractorPageInfo.requireImagePage.path, { replace: true });
        }}
        closeHandler={() => dispatch(userSellTractorSurveyActions.changeHopeSellingPrice(hopeSellingPrice!))}
        confirmButtonText={'다음'}
        prevButtonHandler={() => {
          let prevPath = sellTractorPageInfo.extraInfo2Page.path;
          if (isRegistration) {
            prevPath = sellTractorPageInfo.overviewPage.path;
          }
          navigate(prevPath, { replace: true });
        }}
        steps={isRegistration ? registrationSteps : steps}
        currentStep={currentStep}
      >
        <SurveyForm>
          <SurveyInputSection>
            <SurveyInputHeader inputTitle={'판매 희망가'} isRequired />
            <InputContainer>
              <BasicInputWithExtras
                type={'number'}
                inputValue={hopeSellingPrice}
                placeholder={'예) 6500'}
                onChange={(value: InputValue) => setHopeSellingPrice(value as number)}
              >
                <Surfix>{'만원'}</Surfix>
              </BasicInputWithExtras>
              <PriceHelperText price={hopeSellingPrice!} />
            </InputContainer>
            <QuickPriceAdjuster>
              <PriceAdjustButton
                onClick={() => {
                  ampli.pvmClickAddPriceOnSellTractorSellingPricePage({
                    added_amount: 500000,
                  });

                  if (!hopeSellingPrice) {
                    setHopeSellingPrice(50);
                    return;
                  }

                  setHopeSellingPrice(hopeSellingPrice + 50);
                }}
              >
                <PriceAdjustButtonText>{'+50만'}</PriceAdjustButtonText>
              </PriceAdjustButton>
              <PriceAdjustButton
                onClick={() => {
                  ampli.pvmClickAddPriceOnSellTractorSellingPricePage({
                    added_amount: 1000000,
                  });

                  if (!hopeSellingPrice) {
                    setHopeSellingPrice(100);
                    return;
                  }

                  setHopeSellingPrice(hopeSellingPrice + 100);
                }}
              >
                <PriceAdjustButtonText>{'+100만'}</PriceAdjustButtonText>
              </PriceAdjustButton>
              <PriceAdjustButton
                onClick={() => {
                  ampli.pvmClickAddPriceOnSellTractorSellingPricePage({
                    added_amount: 10000000,
                  });

                  if (!hopeSellingPrice) {
                    setHopeSellingPrice(1000);
                    return;
                  }

                  setHopeSellingPrice(hopeSellingPrice + 1000);
                }}
              >
                <PriceAdjustButtonText>{'+1,000만'}</PriceAdjustButtonText>
              </PriceAdjustButton>
            </QuickPriceAdjuster>
          </SurveyInputSection>
          {openMarketProducts && openMarketProducts.pages[0].length > 0 && (
            <PriceReferenceList>
              <PriceReferenceListTitleSection>
                <PriceReferenceListTitleIcon>{'🔍'}</PriceReferenceListTitleIcon>
                <PriceReferenceTitle>{`${brand} ${model} 가격 추천을 참고 해보세요`}</PriceReferenceTitle>
              </PriceReferenceListTitleSection>
              <PriceReferenceItemsContainer>
                {openMarketProducts.pages[0]
                  .sort((a, b) => {
                    if (!manufacturedYear) return 0;
                    const aYearDiff = Math.abs(a.manufacturedYear - manufacturedYear);
                    const bYearDiff = Math.abs(b.manufacturedYear - manufacturedYear);
                    return aYearDiff - bYearDiff;
                  })
                  .slice(0, 3)
                  .map((product) => (
                    <PriceReferenceItem key={product.id}>
                      <PriceReferenceItemInfoSection>
                        <YearAndOprationTime>{`${String(product.manufacturedYear).slice(2, 4)}년식 · ${Number(
                          product.operationTime,
                        ).toLocaleString()}시간`}</YearAndOprationTime>
                        <Price>{`${formatPrice(product.hopeSellingPrice)}원`}</Price>
                      </PriceReferenceItemInfoSection>
                      <Button
                        borderType={'round'}
                        color={'outlinedGreyLight'}
                        size={'sm'}
                        text={{ content: '가격 적용 하기', size: 'md' }}
                        icon={{ fileName: 'AngleRight', size: 'Small', fill: '#505363', position: 'right' }}
                        onClick={() => {
                          if (brand && model) {
                            ampli.pvmClickApplyPriceOnSellTractorSellingPricePage({
                              brand,
                              model,
                              manufacturedYear,
                              operationTime,
                              selected_model_manufactured_year: product.manufacturedYear,
                              selected_model_operation_time: product.operationTime,
                              selected_model_price: product.hopeSellingPrice,
                            });
                          }
                          setHopeSellingPrice(product.hopeSellingPrice / 10000);
                        }}
                      />
                    </PriceReferenceItem>
                  ))}
              </PriceReferenceItemsContainer>
            </PriceReferenceList>
          )}
        </SurveyForm>
      </SellTractorSurveyPageWrapper>

      <PopupConfirmModal
        isShow={isShowErrorModal}
        hasCloseButton={false}
        closeModal={() => setIsShowErrorModal(false)}
        title={'판매 가격을 입력해 주세요'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => setIsShowErrorModal(false),
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const SurveyInputSection = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px) 24px var(--radius-large, 16px);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
`;

const Surfix = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */

  white-space: nowrap;
`;

const QuickPriceAdjuster = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const PriceAdjustButton = styled.button`
  display: flex;
  height: 32px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const PriceAdjustButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 10.2px */
`;

const PriceReferenceList = styled.div`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  flex: 1 0 0;
  align-self: stretch;

  border-top: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const PriceReferenceListTitleSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const PriceReferenceListTitleIcon = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const PriceReferenceTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const PriceReferenceItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const PriceReferenceItem = styled.div`
  display: flex;
  padding: 24px 0;
  align-items: center;
  gap: 16px;
  align-self: stretch;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const PriceReferenceItemInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`;

const YearAndOprationTime = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const Price = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 13.6px */
`;
